import { openDB } from "idb";
import { LOCALES } from "../constants";
import { loadData } from "../functions/dataLoader";

let equipOrigin;
let subOptionsOrigin;

loadData("equipments.json").then((data) => (equipOrigin = data));
loadData("equipments_sub_options.json").then((data) => (subOptionsOrigin = data));

const DB_NAME_SIMULATOR_SAVIOR = "simulator_savior";
const STORE_NAME = {
    PRESETS_CHAR: "presets_char",
    PRESETS_ARCHIVE: "presets_archive",
    PRESETS_EQUIP: "presets_equip",
    COMMON_TACTICS: "common_tactics",
};
const EQUIP_FLAG = {
    BLANK: true,
    NON_BLANK: false,
};

let dbPromise;

const getDB = () => {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME_SIMULATOR_SAVIOR, 1, {
            upgrade(db) {
                const charPresets = db.createObjectStore(STORE_NAME.PRESETS_CHAR, {
                    keyPath: ["charId", "slot"],
                });

                const archivePresets = db.createObjectStore(STORE_NAME.PRESETS_ARCHIVE, {
                    keyPath: ["charId", "slot"],
                });

                const equipPresets = db.createObjectStore(STORE_NAME.PRESETS_EQUIP, {
                    keyPath: ["charId", "slot"],
                });

                const tactics = db.createObjectStore(STORE_NAME.COMMON_TACTICS, {
                    keyPath: ["id"],
                });

                charPresets.createIndex("charId", "charId");
                archivePresets.createIndex("charId", "charId");
                equipPresets.createIndex("charId", "charId");
                tactics.createIndex("id", "id");
            },
        });
    }

    return dbPromise;
};

const createNewCharPreset = (lang, charId, charName, slot) => {
    return {
        charId: charId,
        slot: slot,
        name: `${charName[lang]} ${slot}`,
        level: 1,
        resonance: 0,
        limitBreak: 0,
        starGrade: 0,
        favor: 1,
        skillLevels: [1, 1, 1, 1],
    };
};

const createNewArchivePreset = (lang, savior, slot) => {
    return {
        charId: savior.id,
        slot: slot,
        name: `${savior.name[lang]} 아카이브 ${slot}`,
        stat: {
            str: savior.journey_base_stat.str,
            hp: savior.journey_base_stat.hp,
            end: savior.journey_base_stat.end,
            focus: savior.journey_base_stat.focus,
            prot: savior.journey_base_stat.prot,
        },
        potentials: [],
    };
};

const createNewEquipData = (lang, part, isBlank) => {
    const equips = equipOrigin.filter((e) => e.part === part && e.tier === 2 && e.grade === 3);
    const equip = equips[0];
    const subs = subOptionsOrigin[equip.tier - 1].stats.filter(
        (e) => !(e.type === equip.main_stats[0].type && e.is_percent === equip.main_stats[0].is_percent),
    );

    if (isBlank) {
        return {
            part: part, // 부위: 무기, 장갑, 갑옷, 목걸이, 반지, 신발 순서 0 ~ 5
            name: "",
            img: "",
            tier: 0,
            enhanced: 0,
            setId: -1,
            grade: -1,
            isEngraved: false,
            mainOption: {
                statType: null,
                isPercent: false,
                baseValue: 0,
                gap: 0,
            },
            subOptions: [],
        };
    }

    return {
        part: part, // 부위: 무기, 장갑, 갑옷, 목걸이, 반지, 신발 순서 0 ~ 5
        name: equip.name[lang],
        img: equip.img,
        tier: equip.tier,
        enhanced: 0,
        setId: equip.set_id,
        grade: equip.grade,
        isEngraved: false,
        mainOption: {
            statType: equip.main_stats[0].type,
            isPercent: equip.main_stats[0].is_percent,
            baseValue: equip.main_stats[0].init_value,
            gap: equip.main_stats[0].gap,
        },
        subOptions: subs.slice(0, 4).map((option) => {
            return {
                statType: option.type,
                isPercent: option.is_percent,
                value: option.values[0],
            };
        }),
    };
};

const createNewEquipPreset = (lang, charId, charName, slot) => {
    const preset = {
        charId: charId,
        slot: slot,
        name: `${charName[lang]} 장비 ${slot}`,
        equips: [],
    };

    for (let i = 0; i < 6; i++) {
        preset.equips.push(createNewEquipData(lang, i, EQUIP_FLAG.NON_BLANK));
    }

    return preset;
};

const createNewBlankEquipPreset = (lang, charId, charName) => {
    const preset = {
        charId: charId,
        slot: 0,
        name: `${charName[lang]} 장비 미착용`,
        equips: [],
    };

    for (let i = 0; i < 6; i++) {
        preset.equips.push(createNewEquipData(lang, i, EQUIP_FLAG.BLANK));
    }

    return preset;
};

const createNewTactics = () => {
    return {
        id: 1,
        savior: 1,
        arcana: {
            level: 1,
            lastType: null,
        },
        attr: {
            sun: {
                level: 1,
                lastType: null,
            },
            moon: {
                level: 1,
                lastType: null,
            },
            star: {
                level: 1,
                lastType: null,
            },
            order: {
                level: 1,
                lastType: null,
            },
            chaos: {
                level: 1,
                lastType: null,
            },
        },
        class: {
            defender: {
                level: 1,
                lastType: null,
            },
            striker: {
                level: 1,
                lastType: null,
            },
            ranger: {
                level: 1,
                lastType: null,
            },
            caster: {
                level: 1,
                lastType: null,
            },
            assassin: {
                level: 1,
                lastType: null,
            },
            supporter: {
                level: 1,
                lastType: null,
            },
        },
        facility: 1,
    };
};

const ensureCharPresets = async (db, presets, lang, charId, charName) => {
    let created = false;

    let count = presets.length;
    if (count > 5) {
        for (let slot = 6; slot <= count; slot++) {
            const exists = presets.find((p) => p.slot === slot);

            db.delete(STORE_NAME.PRESETS_CHAR, [exists.charId, exists.slot]);
        }
        created = true;
    }
    count = 5;
    for (let slot = 1; slot <= count; slot++) {
        const exists = presets.find((p) => p.slot === slot);

        if (!exists) {
            await db.put(STORE_NAME.PRESETS_CHAR, createNewCharPreset(lang, charId, charName, slot));
            created = true;
        } else if (exists.name.includes("[object Object]")) {
            const copied = structuredClone(exists);
            copied.name = copied.name.replace("[object Object]", charName[lang]);

            await db.put(STORE_NAME.PRESETS_CHAR, copied);
            created = true;
        }
    }

    return created;
};

const ensureArchivePresets = async (db, presets, lang, savior) => {
    let created = false;

    let count = presets.length;
    if (count > 5) {
        for (let slot = 6; slot <= count; slot++) {
            const exists = presets.find((p) => p.slot === slot);

            db.delete(STORE_NAME.PRESETS_ARCHIVE, [exists.charId, exists.slot]);
        }
        created = true;
    }
    count = 5;
    for (let slot = 1; slot <= count; slot++) {
        const exists = presets.find((p) => p.slot === slot);

        if (!exists) {
            await db.put(STORE_NAME.PRESETS_ARCHIVE, createNewArchivePreset(lang, savior, slot));
            created = true;
        } else if (exists.name.includes("[object Object]")) {
            const copied = structuredClone(exists);
            copied.name = copied.name.replace("[object Object]", savior.name[lang]);

            await db.put(STORE_NAME.PRESETS_ARCHIVE, copied);
            created = true;
        }
    }

    return created;
};

const ensureEquipPresets = async (db, presets, lang, charId, charName) => {
    let created = false;

    let count = presets.length;
    if (count > 5) {
        for (let slot = 6; slot <= count; slot++) {
            const exists = presets.find((p) => p.slot === slot);
            console.log(exists);
            db.delete(STORE_NAME.PRESETS_EQUIP, [exists.charId, exists.slot]);
        }
        created = true;
    }
    count = 5;
    for (let slot = 1; slot <= count; slot++) {
        const exists = presets.find((p) => p.slot === slot);

        if (!exists) {
            await db.put(STORE_NAME.PRESETS_EQUIP, createNewEquipPreset(lang, charId, charName, slot));
            created = true;
        } else if (exists.equips[0].enhancedOptions) {
            const updated = structuredClone(exists);

            updated.equips.forEach((equip) => {
                if (equip.enhancedOptions && equip.enhancedOptions.length > 0) {
                    equip.enhancedOptions.forEach((option) => {
                        const found = equip.subOptions.find((e) => e.statType === option.statType && e.isPercent === option.isPercent);

                        found.value += option.value;
                    });
                }

                delete equip.enhancedOptions;
            });

            await db.put(STORE_NAME.PRESETS_EQUIP, updated);
            created = true;
        } else if (typeof exists.equips[0].name === "string") {
            const updated = structuredClone(exists);

            updated.equips.forEach((equip) => {
                if (typeof equip.name === "string") {
                    const equips = equipOrigin.filter(
                        (e) =>
                            e.part === equip.part && e.tier === equip.tier && e.grade === equip.grade && e.name[LOCALES.KO] === equip.name,
                    );
                    const obj = equips[0];

                    equip.name = obj.name;
                }
            });

            await db.put(STORE_NAME.PRESETS_EQUIP, updated);
            created = true;
        } else if (exists.name.includes("[object Object]")) {
            const copied = structuredClone(exists);
            copied.name = copied.name.replace("[object Object]", charName[lang]);

            await db.put(STORE_NAME.PRESETS_EQUIP, copied);
            created = true;
        }
    }

    return created;
};

const getCharPresets = async (lang, charId, charName) => {
    const db = await getDB();
    let presets = await db.getAllFromIndex(STORE_NAME.PRESETS_CHAR, "charId", charId);

    if (await ensureCharPresets(db, presets, lang, charId, charName)) {
        presets = await db.getAllFromIndex(STORE_NAME.PRESETS_CHAR, "charId", charId);
    }

    return presets;
};

const getArchivePresets = async (lang, savior) => {
    const db = await getDB();
    let presets = await db.getAllFromIndex(STORE_NAME.PRESETS_ARCHIVE, "charId", savior.id);

    if (await ensureArchivePresets(db, presets, lang, savior)) {
        presets = await db.getAllFromIndex(STORE_NAME.PRESETS_ARCHIVE, "charId", savior.id);
    }

    return presets;
};

const getEquipPresets = async (lang, charId, charName) => {
    const db = await getDB();
    let presets = await db.getAllFromIndex(STORE_NAME.PRESETS_EQUIP, "charId", charId);

    if (await ensureEquipPresets(db, presets, lang, charId, charName)) {
        presets = await db.getAllFromIndex(STORE_NAME.PRESETS_EQUIP, "charId", charId);
    }

    return [createNewBlankEquipPreset(lang, charId, charName), ...presets];
};

const getTactics = async () => {
    const db = await getDB();
    let tactics = await db.getFromIndex(STORE_NAME.COMMON_TACTICS, "id", 1);

    if (!tactics) {
        await db.put(STORE_NAME.COMMON_TACTICS, createNewTactics());
        tactics = await db.getFromIndex(STORE_NAME.COMMON_TACTICS, "id", 1);
    }

    return tactics;
};

const updatePreset = async (store_name, preset) => {
    const db = await getDB();

    if (preset.charId === undefined || preset.slot === undefined) {
        throw new Error(`invalid preset key: charId - ${preset.charId}, slot - ${preset.slot}`);
    }

    await db.put(store_name, preset);
};

const updateCharPreset = async (preset) => {
    await updatePreset(STORE_NAME.PRESETS_CHAR, preset);
};

const updateArchivePreset = async (preset) => {
    await updatePreset(STORE_NAME.PRESETS_ARCHIVE, preset);
};

const updateEquipPreset = async (preset) => {
    await updatePreset(STORE_NAME.PRESETS_EQUIP, preset);
};

const updateTactics = async (tactics) => {
    const db = await getDB();

    await db.put(STORE_NAME.COMMON_TACTICS, tactics);
};

export {
    getCharPresets,
    updateCharPreset,
    getArchivePresets,
    updateArchivePreset,
    getEquipPresets,
    updateEquipPreset,
    getTactics,
    updateTactics,
};
